import React from "react";
import { Button, Column, Conditional, Else, Heading, If, Modal, Row, Section, TruncatedText } from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { MailLink, PhoneLink } from "@hex-insights/router";
import { SlimEmptyView } from "../../../../../../Components/Empty";
import { Icon } from "../../../../../../Components/Icon";
import { Tile } from "../../../../../../Components/Tile";
import { Timeline } from "../../../../../../Components/Timeline";
import { EmployeeDetailQuery, TimelineFilterInput, TimelineObjectType } from "../../../../../../Utilities";
import {
	AddressFormat,
	CampusFormat,
	CountryFormat,
	EmailAddressFormat,
	EmployeeFormat,
	PersonFormat,
	PhoneNumberFormat,
} from "../../../../../Format";
import { EmployeeCombinedForm } from "../../../../../Forms";
import { GuardianLink } from "../../../../../Links";
import * as ModelComponents from "../../../../_General";
import { EmailAddressPersonListModal } from "../../../../EmailAddress";
import { PhoneNumberPersonListModal } from "../../../../PhoneNumber";
import generalStyles from "../general.module.css";
import styles from "./styles.module.css";

export type OverviewBodyProps = {
	employee: EmployeeDetailQuery["employee"];
};

export function OverviewBody({ employee }: OverviewBodyProps) {
	return (
		<Row justify="spaced-start" className={generalStyles["body"]}>
			<Column justify="spaced-start" className={styles["side-bar"]} style={{ flexShrink: 0 }}>
				<DetailsSection employee={employee} />
				<ContactInformation employee={employee} />
			</Column>

			<Column justify="spaced-start" style={{ flexGrow: 1, minWidth: 0 }}>
				<If condition={!!employee.bio}>
					<BioSection employee={employee} />
				</If>
				<TimelineTile employee={employee} />
			</Column>
		</Row>
	);
}

type PageSectionProps = {
	employee: EmployeeDetailQuery["employee"];
};

function DetailsSection({ employee }: PageSectionProps) {
	return (
		<Section className={styles["side-bar__section"] + " " + styles["details-section"]}>
			<Section.Header>
				<Heading.H2 noMargin>Employee Details</Heading.H2>

				<div className={styles["details-section__edit-button__wrapper"]}>
					<DetailFormController employee={employee} />
				</div>
			</Section.Header>
			<Section.Body>
				<Column>
					<Row justify="space-between" className={styles["details-section__detail"]}>
						<span className={styles["details-section__detail__label"]}>Name</span>
						<TruncatedText className={styles["details-section__detail__value"]}>
							{PersonFormat.Fields.name(employee.person.name)}
							{/* Instead of EmployeeFormat.name or PersonFormat.name so that "You" is not displayed */}
						</TruncatedText>
					</Row>
					<Row justify="space-between" className={styles["details-section__detail"]}>
						<span className={styles["details-section__detail__label"]}>Job Title</span>
						<TruncatedText className={styles["details-section__detail__value"]}>
							{EmployeeFormat.Fields.jobTitle(employee.jobTitle)}
						</TruncatedText>
					</Row>
					<Row justify="space-between" className={styles["details-section__detail"]}>
						<span className={styles["details-section__detail__label"]}>Since</span>
						<TruncatedText className={styles["details-section__detail__value"]}>
							{EmployeeFormat.Fields.startDate(employee.startDate)}
						</TruncatedText>
					</Row>
					<Row justify="space-between" className={styles["details-section__detail"]}>
						<span className={styles["details-section__detail__label"]}>Campus</span>
						<TruncatedText className={styles["details-section__detail__value"]}>
							{employee.campuses.map((e) => CampusFormat.name(e)).join(", ")}
						</TruncatedText>
					</Row>
					<Row justify="space-between" className={styles["details-section__detail"]}>
						<span className={styles["details-section__detail__label"]}>Gender</span>
						<TruncatedText className={styles["details-section__detail__value"]}>
							{PersonFormat.Fields.gender(employee.person.gender)}
						</TruncatedText>
					</Row>
					{!!employee.person.guardian && (
						<Row justify="space-between" className={styles["details-section__detail"]}>
							<span className={styles["details-section__detail__label"]}>Guardian</span>
							<TruncatedText className={styles["details-section__detail__value"]}>
								<GuardianLink instance={employee.person.guardian}>View Guardian Profile</GuardianLink>
							</TruncatedText>
						</Row>
					)}
				</Column>
			</Section.Body>
		</Section>
	);
}

const formStateArgs = { editMode: EditMode.WriteOnly };

type DetailFormControllerProps = {
	employee: EmployeeDetailQuery["employee"];
};

function DetailFormController({ employee }: DetailFormControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button variant="link" onClick={openModal} className="button--icon">
				<Icon.Gear size={16} className="block" />
			</Button>

			<Modal.If condition={isModalOpen}>
				<Modal onClose={closeModal} style={{ height: "40rem" }}>
					<Modal.Header>
						<Heading.H2 noMargin>Edit {PersonFormat.Fields.name(employee.person.name)}</Heading.H2>
					</Modal.Header>
					<Modal.Body>
						<EmployeeCombinedForm.ControlledDetail
							employee={employee}
							employeeFormStateArgs={formStateArgs}
							employeeOnSuccess={closeModal}
							personFormStateArgs={formStateArgs}
							personOnSuccess={closeModal}
							userFormStateArgs={formStateArgs}
							userOnSuccess={closeModal}
						/>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

function ContactInformation({ employee }: PageSectionProps) {
	return (
		<Section className={styles["side-bar__section"]}>
			<Section.Header>
				<Heading.H2 noMargin>Contact Information</Heading.H2>
			</Section.Header>
			<Section.Body>
				<Column justify="spaced-start">
					<EmailAddressesSubSection employee={employee} />

					<PhoneNumbersSubSection employee={employee} />

					<Heading.H3 noMargin style={{ fontSize: "1.125rem" }}>
						Addresses
					</Heading.H3>
					<Column>
						<Conditional>
							<If condition={employee.person.addresses.length === 0}>
								<SlimEmptyView>No addresses</SlimEmptyView>
							</If>
							<Else>
								{employee.person.addresses.map((address) => (
									<Row key={address.id} justify="space-between" className={styles["details-section__detail"]}>
										<span className={styles["details-section__detail__label"]}>
											{EmailAddressFormat.Fields.name(address.name) || "Address"}
										</span>
										<div className={styles["details-section__detail__value"]}>
											{AddressFormat.Fields.addressLine1(address.addressLine1)}
											<br />
											<If condition={!!address.addressLine2}>
												{AddressFormat.Fields.addressLine2(address.addressLine2)}
												<br />
											</If>
											<span>{AddressFormat.Fields.city(address.city)}, </span>
											<span>{AddressFormat.Fields.postalCode(address.postalCode)}</span> <br />
											<If condition={!!address.stateOrProvince}>
												<span>{AddressFormat.Fields.stateOrProvince(address.stateOrProvince)}, </span>
											</If>
											<span>{CountryFormat.name(address.country)}</span> <br />
										</div>
									</Row>
								))}
							</Else>
						</Conditional>
					</Column>
				</Column>
			</Section.Body>
		</Section>
	);
}

type ContactInformationSubSectionProps = {
	employee: EmployeeDetailQuery["employee"];
};

function EmailAddressesSubSection({ employee }: ContactInformationSubSectionProps) {
	const emailAddresses = React.useMemo(() => {
		if (employee.person.emailAddresses.some((e) => e.emailAddress === employee.workEmailAddress)) {
			return employee.person.emailAddresses;
		}
		return [
			{
				id: employee.id,
				emailAddress: employee.workEmailAddress,
				isPrimary: true,
				name: "Work",
			},
			...employee.person.emailAddresses,
		];
	}, [employee]);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Row justify="space-between" align="center">
				<Heading.H3 noMargin style={{ fontSize: "1.125rem" }}>
					Email Addresses
				</Heading.H3>

				<Button variant="link" size="small" onClick={openModal} className="button--icon">
					<Icon.Gear size={16} className="block" />
				</Button>
			</Row>
			<Column>
				<Conditional>
					<If condition={emailAddresses.length === 0}>
						<SlimEmptyView>No email addresses</SlimEmptyView>
					</If>
					<Else>
						{emailAddresses.map((emailAddress) => (
							<Row key={emailAddress.id} justify="space-between" className={styles["details-section__detail"]}>
								<span className={styles["details-section__detail__label"]}>
									<Row justify="spaced-start" align="center">
										{EmailAddressFormat.Fields.name(emailAddress.name) || "Email"}
										<If condition={emailAddress.isPrimary}>
											<Icon.Star color="var(--theme-colors__yellow)" size={14} weight="fill" />
										</If>
									</Row>
								</span>
								<TruncatedText className={styles["details-section__detail__value"]}>
									<MailLink email={emailAddress.emailAddress} className="link--no-text-decoration">
										{EmailAddressFormat.Fields.emailAddress(emailAddress.emailAddress)}
									</MailLink>
								</TruncatedText>
							</Row>
						))}
					</Else>
				</Conditional>
			</Column>

			<Modal.If condition={isModalOpen}>
				<EmailAddressPersonListModal
					person={employee.person}
					emailAddresses={employee.person.emailAddresses}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

function PhoneNumbersSubSection({ employee }: ContactInformationSubSectionProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Row justify="space-between" align="center">
				<Heading.H3 noMargin style={{ fontSize: "1.125rem" }}>
					Phone Numbers
				</Heading.H3>

				<Button variant="link" size="small" onClick={openModal} className="button--icon">
					<Icon.Gear size={16} className="block" />
				</Button>
			</Row>
			<Column>
				<Conditional>
					<If condition={employee.person.phoneNumbers.length === 0}>
						<SlimEmptyView>No phone numbers</SlimEmptyView>
					</If>
					<Else>
						{employee.person.phoneNumbers.map((phoneNumber) => (
							<Row key={phoneNumber.id} justify="space-between" className={styles["details-section__detail"]}>
								<span className={styles["details-section__detail__label"]}>
									<Row justify="spaced-start" align="center">
										{EmailAddressFormat.Fields.name(phoneNumber.name) || "Phone"}
										<If condition={phoneNumber.isPrimary}>
											<Icon.Star color="var(--theme-colors__yellow)" size={14} weight="fill" />
										</If>
									</Row>
								</span>
								<TruncatedText className={styles["details-section__detail__value"]}>
									<PhoneLink phone={phoneNumber.phoneNumber} className="link--no-text-decoration">
										{PhoneNumberFormat.Fields.phoneNumber(phoneNumber.phoneNumber)}
									</PhoneLink>
								</TruncatedText>
							</Row>
						))}
					</Else>
				</Conditional>
			</Column>

			<Modal.If condition={isModalOpen}>
				<PhoneNumberPersonListModal
					person={employee.person}
					phoneNumbers={employee.person.phoneNumbers}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

function BioSection({ employee }: PageSectionProps) {
	return (
		<div className={styles["bio-section"]}>
			<Column justify="spaced-start" verticalSpacing="0.375rem" className={styles["bio-section__content"]}>
				<span className={styles["bio-section__heading"]}>
					About {PersonFormat.Fields.givenName(employee.person.givenName)}
				</span>

				<ModelComponents.ExpandableText lines={2} className={styles["bio-section__body"]}>
					{employee.bio}
				</ModelComponents.ExpandableText>
			</Column>
		</div>
	);
}

function TimelineTile({ employee }: PageSectionProps) {
	const filter = React.useMemo<TimelineFilterInput>(
		() => ({
			objectTypeIn: [
				TimelineObjectType.AcademicTerm,
				TimelineObjectType.Announcement,
				TimelineObjectType.CalendarEvent,
				TimelineObjectType.Post,
				TimelineObjectType.StudentNote,
			],
			employeeID: employee.id,
		}),
		[employee.id],
	);

	return (
		<Tile style={{ flexGrow: 1 }}>
			<Tile.Body>
				<Timeline filter={filter} pageSize={10} style={{ height: "100%" }} />
			</Tile.Body>
		</Tile>
	);
}
